import * as React from "react";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { Accordion } from "../../Elements/Accordion";
import { useDrawingContext } from "../../Contexts/DrawingContext";
import { DrawYards } from "./DrawYards";
import { DrawRoads } from "./DrawRoads";
import { DrawLVMRoads } from "./DrawLVMRoads";
import { DrawProperty } from "./DrawProperty";
import { useRegistrationContext } from "../../Contexts/RegistrationContext";
import { DrawType, PageId } from "../../Map/enums";
import { getMap } from "../DrawingForm";
import { Search } from "./Search";
import { AccordionInfo } from "../../Elements/AccordionInfo";
import { getIntersectedForestryCount, getLVMForestry } from "../../Map/rest";
import { useAppContext } from "../../Contexts/AppContext";
import "./DrawingTools.scss";

const formId = PageId.DrawingForm;
const minSetFeature = 1;

const infoStrings = {
    scaleForPointsWarning: "Šī objekta iezīmēšana kartē ir iespējama mērogā no 500 m, lūdzu pietuviniet karti!",
    scaleForLineAndAreaWarning: "Šī objekta iezīmēšana kartē ir iespējama mērogā no 2 km, lūdzu pietuviniet karti!",
    propertyAndPointsWarning: "Īpašuma un krautuves vietas atzīme kartē ir obligāta!",
    propertyWarning: "Īpašuma vietas atzīme kartē ir obligāta!",
    pointWarning: "Krautuves vietas atzīme kartē ir obligāta!",
    propertyNoteWarning: "Nepieciešams īpašuma nosaukums!"
};

export const DrawingTools = (): JSX.Element => {
    const [validate, setValidate] = useState<number>(0);
    const [propertyWarning, setPropertyWarning] = useState<boolean>(true);
    const [pointWarning, setPointWarning] = useState<boolean>(true);
    const [warnings, setWarnings] = useState<string[]>([]);
    const [maxHeight, setMaxHeight] = useState<number>(0);

    const { scaleWarning, setScaleWarning, drawType } = useAppContext();

    const { drawingMenuVisible } = useRegistrationContext();
    const {
        formFeature,
        validateForm,
        onFormUpdated,
        drawnFeatures,
        setOverlay,
        setLvmForestryData,
        setLvmForestryCount,
        setDrawingInfoVisible,
        setSearchInfoVisible
    } = useRegistrationContext();
    const { propertyNote } = useDrawingContext();

    const drawTypeRef = useRef<DrawType>(undefined);

    useEffect(() => {
        getMap().on("moveend", () => setScaleWarning(isDrawingAllowed()));
        setMaxHeight(document.getElementsByClassName("map-container")[0].clientHeight - 100);

        if (!localStorage.getItem("timberFlowFormPopup")) {
            localStorage.setItem("timberFlowFormPopup", JSON.stringify("shown"));
            setDrawingInfoVisible(true);
        }
    }, []);

    useEffect(() => {
        const validateThisForm =
            ((validateForm.split("-")[0] as unknown) as PageId) === formId ? Number(validateForm.split("-")[1]) : 1;
        if (validateThisForm !== validate) {
            setValidate(validateThisForm);
            !warnings.length && !propertyWarning && !pointWarning && onFormValid();
        }
    }, [validateForm, validate, propertyWarning, pointWarning]);

    const onFormValid = async () => {
        setOverlay({ isLoading: true });

        const forestry = await getLVMForestry([drawnFeatures.find((df) => df.get("DRAWTYPE") === "PointYard")]);
        if (forestry && forestry.data) {
            const forestryData = {
                LVMDistrictCode: forestry.data.LVMDISTRICTCODE,
                LVMDistrictName: forestry.data.LVMDISTRICTNAME,
                LVMForestryCode: forestry.data.LVMFORESTRYCODE,
                LVMForestryName: forestry.data.LVMFORESTRYNAME,
                LVMManagerName: forestry.data.MANAGERNAME,
                LVMManagerPhone: forestry.data.MANAGERPHONE,
                LVMManagerMail: forestry.data.MANAGEREMAIL
            };
            formFeature.setProperties(forestryData);
            setLvmForestryData(forestryData);
        }

        const forestryCount = await getIntersectedForestryCount(drawnFeatures);

        forestryCount &&
            forestryCount.data &&
            forestryCount.data.count &&
            setLvmForestryCount(forestryCount.data.count);
        setOverlay(null);

        onFormUpdated();
    };

    useEffect(() => {
        setPropertyWarning(isPropertySelected);
        setPointWarning(isPointSelected);
    }, [drawnFeatures]);

    useEffect(() => {
        drawTypeRef.current = drawType;
        setScaleWarning(isDrawingAllowed());
    }, [drawType]);

    useEffect(() => {
        const newWarnings = [];
        scaleWarning &&
            newWarnings.push(
                drawTypeRef.current === DrawType.PolylineRoad ||
                    drawTypeRef.current === DrawType.PolylineLvmRoad ||
                    drawTypeRef.current === DrawType.PolygonProperty
                    ? infoStrings.scaleForLineAndAreaWarning
                    : drawTypeRef.current === DrawType.PointYard && infoStrings.scaleForPointsWarning
            );
        pointWarning && newWarnings.push(infoStrings.pointWarning);
        propertyWarning && newWarnings.push(infoStrings.propertyWarning);
        !propertyWarning && !propertyNote && newWarnings.push(infoStrings.propertyNoteWarning);

        setWarnings(newWarnings);
    }, [scaleWarning, propertyWarning, pointWarning, propertyNote, drawType]);

    const isPointSelected =
        drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.PointYard).length < minSetFeature;
    const isPropertySelected =
        drawnFeatures.filter((f) => f.get("DRAWTYPE") === DrawType.PolygonProperty).length !== minSetFeature;

    const isDrawingAllowed = () =>
        !drawTypeRef || drawTypeRef.current === undefined
            ? false
            : drawTypeRef.current === DrawType.PolylineRoad ||
              drawTypeRef.current === DrawType.PolylineLvmRoad ||
              drawTypeRef.current === DrawType.PolygonProperty
            ? //@ts-ignore
              !(document.mapScale <= 111000) // 2km
            : //@ts-ignore
              !(document.mapScale <= 28000); // 500m

    return (
        <>
            <div className={classNames("triangle-top", { hidden: !drawingMenuVisible })}></div>
            <Accordion maxHeight={maxHeight}>
                <div
                    className={classNames("scrollable", { collapsed: !drawingMenuVisible })}
                    onScroll={() => setSearchInfoVisible(false)}
                >
                    <Search></Search>
                    <DrawProperty></DrawProperty>
                    <DrawYards></DrawYards>
                    <DrawRoads></DrawRoads>
                    <DrawLVMRoads></DrawLVMRoads>
                </div>
                {warnings.length ? <AccordionInfo warnings={warnings}></AccordionInfo> : null}
            </Accordion>
        </>
    );
};
